import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import $api from 'http/index'
import Notification from 'utils/Notifications'
import { PageHeader, Card, UnvButton, UnvInput, UnvModal, EmptyState, Icon } from 'components'

const KIND_OPTIONS = [
  { v: 'training', t: 'Тренировочный' },
  { v: 'control', t: 'Контрольная точка' },
  { v: 'exam', t: 'Экзамен' },
  { v: 'survey', t: 'Опрос' },
]

const QKIND_OPTIONS = [
  { v: 'single', t: 'Один правильный' },
  { v: 'multi', t: 'Несколько правильных' },
  { v: 'free', t: 'Свободный ответ' },
]

const emptyOption = () => ({ id: null, text: '', is_correct: false, position: 0 })
const emptyQuestion = () => ({
  id: null,
  text: '',
  kind: 'single',
  points: 1,
  position: 0,
  options: [emptyOption(), emptyOption()],
})

const TestBuilderPage = () => {
  const { t } = useTranslation('common')

  const params = useParams()
  const navigate = useNavigate()
  const id = params?.id

  const [meta, setMeta] = useState({
    title: '',
    description: '',
    kind: 'training',
    subject: '',
    theme: '',
    attempts_allowed: 1,
    time_limit_sec: 0,
    pass_threshold: 60,
    random_questions: false,
    proctoring_enabled: false,
    require_fullscreen: false,
    detect_tab_switch: false,
    photo_required: false,
    screenshot_required: false,
    window_from: '',
    window_to: '',
  })
  const [questions, setQuestions] = useState([])
  const [subjects, setSubjects] = useState([])
  const [themes, setThemes] = useState([])
  const [bankOpen, setBankOpen] = useState(false)
  const [bank, setBank] = useState([])
  const [bankFilter, setBankFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })
  const dragIndex = useRef(null)

  const flash = (message, type = 'success') => setNotify({ isOpen: true, message, type })

  const loadSubjects = async () => {
    try {
      const r = await $api.get('/syllabus/subjects/')
      setSubjects(r.data?.results || r.data || [])
    } catch {
      setSubjects([])
    }
  }

  const loadThemes = async (subjectId) => {
    if (!subjectId) {
      setThemes([])
      return
    }
    try {
      const r = await $api.get('/syllabus/themes/', { params: { subject: subjectId } })
      setThemes(r.data?.results || r.data || [])
    } catch {
      setThemes([])
    }
  }

  const loadTest = async () => {
    if (!id) return
    setLoading(true)
    try {
      const r = await $api.get(`/worktests/tests/${id}/`).catch(() => $api.get(`/worktest/${id}/`))
      const t = r.data || {}
      setMeta({
        title: t.title || '',
        description: t.description || t.body || '',
        kind: t.kind || 'training',
        subject: t.subject || '',
        theme: t.theme || '',
        attempts_allowed: t.attempts_allowed ?? 1,
        time_limit_sec: t.time_limit_sec ?? 0,
        pass_threshold: t.pass_threshold ?? 60,
        random_questions: !!t.random_questions,
        proctoring_enabled: !!t.proctoring_enabled,
        require_fullscreen: !!t.require_fullscreen,
        detect_tab_switch: !!t.detect_tab_switch,
        photo_required: !!t.photo_required,
        screenshot_required: !!t.screenshot_required,
        window_from: t.window_from || '',
        window_to: t.window_to || '',
      })
      setQuestions(
        (t.questions || t.part_worktest || []).map((q, i) => ({
          id: q.id,
          text: q.text || q.body || '',
          kind: q.kind || (q.one_answer_option_or_multiple_choice ? 'single' : 'multi'),
          points: q.points ?? 1,
          position: q.position ?? q.part_number ?? i,
          options: (q.options || q.answer_part_worktest || []).map((o, j) => ({
            id: o.id,
            text: o.text || o.title || '',
            is_correct: !!(o.is_correct ?? o.right_answer),
            position: o.position ?? o.answer_number ?? j,
          })),
        })),
      )
    } catch {
      flash('Не удалось загрузить тест', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSubjects()
    loadTest()
  }, [id])

  useEffect(() => {
    loadThemes(meta.subject)
  }, [meta.subject])

  const setMetaField = (k, v) => setMeta((m) => ({ ...m, [k]: v }))

  const addQuestion = () =>
    setQuestions((arr) => [...arr, { ...emptyQuestion(), position: arr.length }])

  const removeQuestion = (idx) =>
    setQuestions((arr) =>
      arr.filter((_, i) => i !== idx).map((q, i) => ({ ...q, position: i })),
    )

  const moveQuestion = (idx, dir) =>
    setQuestions((arr) => {
      const next = [...arr]
      const j = idx + dir
      if (j < 0 || j >= next.length) return next
      ;[next[idx], next[j]] = [next[j], next[idx]]
      return next.map((q, i) => ({ ...q, position: i }))
    })

  const onDragStart = (idx) => (e) => {
    dragIndex.current = idx
    e.dataTransfer.effectAllowed = 'move'
  }

  const onDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const onDrop = (idx) => (e) => {
    e.preventDefault()
    const from = dragIndex.current
    if (from == null || from === idx) return
    setQuestions((arr) => {
      const next = [...arr]
      const [moved] = next.splice(from, 1)
      next.splice(idx, 0, moved)
      return next.map((q, i) => ({ ...q, position: i }))
    })
    dragIndex.current = null
  }

  const setQuestionField = (idx, k, v) =>
    setQuestions((arr) => arr.map((q, i) => (i === idx ? { ...q, [k]: v } : q)))

  const addOption = (qIdx) =>
    setQuestions((arr) =>
      arr.map((q, i) =>
        i === qIdx
          ? { ...q, options: [...q.options, { ...emptyOption(), position: q.options.length }] }
          : q,
      ),
    )

  const removeOption = (qIdx, oIdx) =>
    setQuestions((arr) =>
      arr.map((q, i) =>
        i === qIdx
          ? {
              ...q,
              options: q.options
                .filter((_, j) => j !== oIdx)
                .map((o, j) => ({ ...o, position: j })),
            }
          : q,
      ),
    )

  const setOptionField = (qIdx, oIdx, k, v) =>
    setQuestions((arr) =>
      arr.map((q, i) =>
        i === qIdx
          ? {
              ...q,
              options: q.options.map((o, j) =>
                j === oIdx
                  ? { ...o, [k]: v }
                  : k === 'is_correct' && q.kind === 'single' && v
                  ? { ...o, is_correct: false }
                  : o,
              ),
            }
          : q,
      ),
    )

  const openBank = async () => {
    setBankOpen(true)
    try {
      const r = await $api.get('/worktests/questions/', { params: { q: bankFilter } })
      setBank(r.data?.results || r.data || [])
    } catch {
      setBank([])
    }
  }

  const importFromBank = (selected) => {
    setQuestions((arr) => [
      ...arr,
      ...selected.map((qb, k) => ({
        id: null,
        text: qb.text,
        kind: qb.kind || 'single',
        points: Number(qb.points) || 1,
        position: arr.length + k,
        options: (qb.options || []).map((o, j) => ({
          id: null,
          text: o.text || o,
          is_correct: !!o.is_correct,
          position: j,
        })),
      })),
    ])
    setBankOpen(false)
  }

  const buildPayload = (publish = false) => {
    const payload = {
      ...meta,
      activate: publish ? true : meta.activate || false,
      questions,
    }
    if (!meta.window_from) delete payload.window_from
    if (!meta.window_to) delete payload.window_to
    if (!meta.subject) delete payload.subject
    if (!meta.theme) delete payload.theme
    return payload
  }

  const save = async (publish = false) => {
    setLoading(true)
    try {
      const payload = buildPayload(publish)
      const r = id
        ? await $api.patch(`/worktests/tests/${id}/`, payload).catch(() =>
            $api.patch(`/worktest/${id}/`, payload),
          )
        : await $api.post('/worktests/tests/', payload)
      flash(publish ? 'Опубликовано' : 'Сохранено')
      if (!id && r.data?.id) navigate(`/test-builder/${r.data.id}`)
    } catch {
      flash('Ошибка сохранения', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ margin: '0 auto' }}>
      <PageHeader
        icon={<Icon name="test" size={28} />}
        title={id ? t('auto.testbuilder.edit', 'Редактирование теста') : t('auto.testbuilder.new', 'Новый тест')}
        subtitle={t('auto.testbuilder.subtitle', 'Конструктор тестов с прокторингом')}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <UnvButton variant="ghost" iconLeft="arrow-left" onClick={() => navigate('/tests/')}>{t('auto.testbuilder.to_list', 'К списку')}</UnvButton>
            <UnvButton variant="secondary" iconLeft="save" onClick={() => save(false)} loading={loading}>{t('auto.testbuilder.save', 'Сохранить')}</UnvButton>
            <UnvButton variant="primary" iconLeft="check" onClick={() => save(true)} loading={loading}>{t('auto.testbuilder.publish', 'Опубликовать')}</UnvButton>
          </div>
        }
      />
      <Card padding="md" style={{ marginBottom: 16 }}>
        <Card.Body>
        <div style={{ display: 'grid', gap: 12 }}>
          <input
            className="unv-input"
            placeholder={t('auto.testpage_testbuilderpage.k5')}
            value={meta.title}
            onChange={(e) => setMetaField('title', e.target.value)}
          />
          <textarea
            className="unv-textarea"
            placeholder={t('assignments.description_label')}
            value={meta.description}
            onChange={(e) => setMetaField('description', e.target.value)}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <select
              className="unv-select"
              value={meta.kind}
              onChange={(e) => setMetaField('kind', e.target.value)}
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k.v} value={k.v}>
                  {k.t}
                </option>
              ))}
            </select>
            <select
              className="unv-select"
              value={meta.subject}
              onChange={(e) => setMetaField('subject', e.target.value)}
            >
              <option value="">{t('auto.testpage_questionbankpage.k4')}</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name_subject || s.name || `#${s.id}`}
                </option>
              ))}
            </select>
            <select
              className="unv-select"
              value={meta.theme}
              onChange={(e) => setMetaField('theme', e.target.value)}
              disabled={!meta.subject}
            >
              <option value="">{t('auto.testpage_testbuilderpage.k1')}</option>
              {themes.map((th) => (
                <option key={th.id} value={th.id}>
                  {th.name || th.title || `#${th.id}`}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <label>
              Попыток
              <input
                className="unv-input"
                type="number"
                min={1}
                value={meta.attempts_allowed}
                onChange={(e) => setMetaField('attempts_allowed', Number(e.target.value))}
              />
            </label>
            <label>
              Лимит времени, сек
              <input
                className="unv-input"
                type="number"
                min={0}
                value={meta.time_limit_sec}
                onChange={(e) => setMetaField('time_limit_sec', Number(e.target.value))}
              />
            </label>
            <label>
              Порог сдачи, %
              <input
                className="unv-input"
                type="number"
                min={0}
                max={100}
                value={meta.pass_threshold}
                onChange={(e) => setMetaField('pass_threshold', Number(e.target.value))}
              />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              Окно: с
              <input
                className="unv-input"
                type="datetime-local"
                value={meta.window_from}
                onChange={(e) => setMetaField('window_from', e.target.value)}
              />
            </label>
            <label>
              Окно: до
              <input
                className="unv-input"
                type="datetime-local"
                value={meta.window_to}
                onChange={(e) => setMetaField('window_to', e.target.value)}
              />
            </label>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
              marginTop: 8,
            }}
          >
            <label>
              <input
                type="checkbox"
                checked={meta.random_questions}
                onChange={(e) => setMetaField('random_questions', e.target.checked)}
              />{' '}
              Случайный порядок
            </label>
            <label>
              <input
                type="checkbox"
                checked={meta.proctoring_enabled}
                onChange={(e) => setMetaField('proctoring_enabled', e.target.checked)}
              />{' '}
              Прокторинг включён
            </label>
            <label>
              <input
                type="checkbox"
                checked={meta.require_fullscreen}
                onChange={(e) => setMetaField('require_fullscreen', e.target.checked)}
              />{' '}
              Полноэкранный режим
            </label>
            <label>
              <input
                type="checkbox"
                checked={meta.detect_tab_switch}
                onChange={(e) => setMetaField('detect_tab_switch', e.target.checked)}
              />{' '}
              Фиксировать смену вкладки
            </label>
            <label>
              <input
                type="checkbox"
                checked={meta.photo_required}
                onChange={(e) => setMetaField('photo_required', e.target.checked)}
              />{' '}
              Фото с веб-камеры
            </label>
            <label>
              <input
                type="checkbox"
                checked={meta.screenshot_required}
                onChange={(e) => setMetaField('screenshot_required', e.target.checked)}
              />{' '}
              Скриншот экрана
            </label>
          </div>
        </div>
        </Card.Body>
      </Card>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <UnvButton variant="primary" iconLeft="plus" onClick={addQuestion}>
          {t('auto.testpage_testbuilderpage.k2')}
        </UnvButton>
        <UnvButton variant="secondary" iconLeft="folder" onClick={openBank}>
          {t('auto.testpage_testbuilderpage.k3')}
        </UnvButton>
      </div>

      {questions.map((q, qIdx) => (
        <div
          key={qIdx}
          className="unv-card"
          style={{ marginBottom: 12 }}
          draggable
          onDragStart={onDragStart(qIdx)}
          onDragOver={onDragOver}
          onDrop={onDrop(qIdx)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontWeight: 600, minWidth: 32 }}>#{qIdx + 1}</span>
            <select
              className="unv-select"
              style={{ width: 200 }}
              value={q.kind}
              onChange={(e) => setQuestionField(qIdx, 'kind', e.target.value)}
            >
              {QKIND_OPTIONS.map((k) => (
                <option key={k.v} value={k.v}>
                  {k.t}
                </option>
              ))}
            </select>
            <input
              className="unv-input"
              style={{ width: 100 }}
              type="number"
              min={0}
              value={q.points}
              onChange={(e) => setQuestionField(qIdx, 'points', Number(e.target.value))}
            />
            <span style={{ flex: 1 }} />
            <button
              className="unv-btn unv-btn--ghost unv-btn--sm"
              onClick={() => moveQuestion(qIdx, -1)}
            >
              ↑
            </button>
            <button
              className="unv-btn unv-btn--ghost unv-btn--sm"
              onClick={() => moveQuestion(qIdx, 1)}
            >
              ↓
            </button>
            <button
              className="unv-btn unv-btn--danger unv-btn--sm"
              onClick={() => removeQuestion(qIdx)}
            >
              Удалить
            </button>
          </div>
          <textarea
            className="unv-textarea"
            placeholder={t('surveyForm.question_text')}
            value={q.text}
            onChange={(e) => setQuestionField(qIdx, 'text', e.target.value)}
          />
          {q.kind !== 'free' && q.kind !== 'text' && (
            <div style={{ marginTop: 12 }}>
              {q.options.map((o, oIdx) => (
                <div
                  key={oIdx}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
                >
                  <input
                    type={q.kind === 'single' ? 'radio' : 'checkbox'}
                    name={`q${qIdx}`}
                    checked={!!o.is_correct}
                    onChange={(e) => setOptionField(qIdx, oIdx, 'is_correct', e.target.checked)}
                  />
                  <input
                    className="unv-input"
                    placeholder={`Вариант ${oIdx + 1}`}
                    value={o.text}
                    onChange={(e) => setOptionField(qIdx, oIdx, 'text', e.target.value)}
                  />
                  <button
                    className="unv-btn unv-btn--ghost unv-btn--sm"
                    onClick={() => removeOption(qIdx, oIdx)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                className="unv-btn unv-btn--ghost unv-btn--sm"
                onClick={() => addOption(qIdx)}
              >
                + Добавить вариант
              </button>
            </div>
          )}
        </div>
      ))}

      <UnvModal
        open={bankOpen}
        onClose={() => setBankOpen(false)}
        title={t('menu.questionBank')}
        size="lg"
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <UnvInput
            placeholder={t('auto.testpage_testbuilderpage.k6')}
            iconLeft="search"
            value={bankFilter}
            onChange={(e) => setBankFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && openBank()}
          />
          <UnvButton variant="primary" iconLeft="search" onClick={openBank}>{t('archive.ocr_find_btn')}</UnvButton>
        </div>
        <BankPicker items={bank} onConfirm={importFromBank} t={t} />
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setBankOpen(false)}>{t('common.close')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

const BankPicker = ({ items, onConfirm, t }) => {
  const [picked, setPicked] = useState({})
  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }))
  const selected = items.filter((it) => picked[it.id])
  if (!items || items.length === 0) {
    return <EmptyState iconName="folder" title={t ? t('auto.testpage_testbuilderpage.k4') : 'Нет данных'} variant="compact" />
  }
  return (
    <>
      {items.map((it) => (
        <div
          key={it.id}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}
        >
          <input
            type="checkbox"
            checked={!!picked[it.id]}
            onChange={() => toggle(it.id)}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{it.text}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              {(it.tags || []).join(', ')} · {t ? t('auto.testbuilder.difficulty', 'сложность') : 'сложность'}: {it.difficulty || '—'} · {it.kind}
            </div>
          </div>
        </div>
      ))}
      {selected.length > 0 && (
        <UnvButton variant="primary" iconLeft="download" style={{ marginTop: 8 }} onClick={() => onConfirm(selected)}>
          {t ? t('auto.testbuilder.import_selected', 'Импортировать выбранные') : 'Импортировать'} ({selected.length})
        </UnvButton>
      )}
    </>
  )
}

export default TestBuilderPage
